import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import { useContext, useState } from 'react';
import { RootStoreContext } from '../../global/storeContext';
import { InputFieldMoney } from '../formComponents/inputfieldmoney';
import {
  LargeInteracIcon,
  SmallInteracIcon,
  SmallEFTIcon,
  SmallCreditCardIcon,
} from '../icon/vendorIcons';
import { RightDrawer } from '../uiComponents/rightDrawer';
import { NumericFormat } from 'react-number-format';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { usePaymentAmountLimit } from '../../hooks/usePaymentAmountLimit';
import { FinixPaymentForm } from './finix';
import { AutoDebitSetupStatus, IBill } from 'realhaus-sdk';
import { getTrxFeeBreakdown, PaymentMethod } from '../../utils/bill';
import { Alert, AlertTitle } from '@mui/material';
import { timeout } from '../../utils/asyncUtils';
import { paths } from '../../routes';
import { StripePaymentForm } from './stripe';
import { useIdentityGate } from '../../hooks/useIdentityGate';
import { CompleteAccountSetupDialog } from '../rentApplication/dialogs/completeAccountSetupDialog';

interface IPaymentCheckout {
  open: boolean;
  handleClose: () => void;
  handleComplete: () => void;
  handleFailure: () => void;
}

interface IBillCheckout extends IPaymentCheckout {
  type: 'BILL';
  leaseId: string;
  billId: string;
}

interface ICreditReportSeatsCheckout extends IPaymentCheckout {
  type: 'CREDIT_REPORT_SEAT';
}

type PaymentCheckoutProps = IBillCheckout | ICreditReportSeatsCheckout;

export const PaymentCheckout: React.FC<PaymentCheckoutProps> = observer((prop) => {
  const { uiStore, userStore, leaseStore, walletStore, analyticsStore } =
    useContext(RootStoreContext);
  const { checkAndProceed, gateDialogProps } = useIdentityGate();
  const [amount, setAmount] = useState(0);
  const [openInteracConfirmModal, setOpenInteracConfirmModal] = useState(false);
  const [openFinixTokenModal, setOpenFinixTokenModal] = useState(false);
  const [openStripeModal, setOpenStripeModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>();
  const [feeBreakdown, setFeeBreakdown] =
    useState<{ [key in PaymentMethod]: { fee: number; netAmount: number } }>();
  const [paramountInteracPayload, setParamountInteracPayload] = useState<{
    details: string;
    merchantId: string;
    iv: string;
    paymentUrl: string;
    target?: string;
  }>();
  const [leaseBill, setLeaseBill] = useState<IBill | undefined>();
  const [noOfSeats, setNoOfSeats] = React.useState<number>(1);
  const [popupBlockerEnabled, setPopupBlockerEnabled] = React.useState<boolean>(false);
  const [isPADSetupComplete, setIsPADSetupComplete] = useState(false);
  const [isAutoPayPolicyEnabled, setIsAutoPayPolicyEnabled] = useState(false);

  const {
    paymentVendorsList,
    transactionFees,
    flaggedUser,
    creditReportSeatAmount,
    autoDebitEnabled,
    paymentOptionBankEnabled,
    paymentOptionCcEnabled,
    paymentOptionInteracEnabled,
  } = useFlags();

  const { exceedsLimit, limitMessage } = usePaymentAmountLimit();

  const { type, open, handleClose, handleComplete, handleFailure } = prop;

  const pCommerceFormRef = useRef<HTMLFormElement>(null);

  const isBill = type === 'BILL';
  const isCreditReportSeat = type === 'CREDIT_REPORT_SEAT';

  useEffect(() => {
    if (isBill) {
      if (!prop.billId || !prop.leaseId) {
        // clear fields
        setAmount(0);
        setSelectedPaymentMethod(undefined);
        setFeeBreakdown(undefined);
        setIsPADSetupComplete(false);
        setIsAutoPayPolicyEnabled(false);
        console.log(`-- Empty Bill ID`);
        return;
      }

      uiStore.showLoading();
      leaseStore
        .getLeaseBill(prop.leaseId, prop.billId)
        .then((bill) => {
          if (!bill || !bill.status || !bill.amount) {
            return;
          }

          if (isNaN(bill.amount) || bill.amount < 0) return;
          console.log(`--Bill Amount = ${bill.amount}`);
          setLeaseBill(bill);
          updateAmount(Number(bill.amount));
        })
        .catch(() => { })
        .finally(() => {
          uiStore.hideLoading();
        });

      leaseStore
        .getLeaseAgreementById(prop.leaseId)
        .then(async (lease) => {
          // The lease PAD belongs to the tenant who set it up; only they may pay by bank.
          const padCustomerId = lease?.autoDebitSetup?.stripe?.customerId;
          const profile = userStore.userId
            ? await userStore.getUserProfile(userStore.userId)
            : undefined;
          setIsPADSetupComplete(
            lease?.autoDebitSetup?.status === AutoDebitSetupStatus.COMPLETED &&
              !!padCustomerId &&
              profile?.stripeCustomerId === padCustomerId,
          );
          setIsAutoPayPolicyEnabled(lease?.policies?.autoPaymentSetup === true);
        })
        .catch(() => {
          setIsPADSetupComplete(false);
          setIsAutoPayPolicyEnabled(false);
        });
    }

    if (isCreditReportSeat) {
      if (!creditReportSeatAmount) {
        // clear fields
        setAmount(0);
        setSelectedPaymentMethod(PaymentMethod.CREDITCARD);
        setFeeBreakdown(undefined);
        console.log(`CreditReport seat amount not set`);
        return;
      }

      updateAmount(Number(creditReportSeatAmount));
    }
  }, [type]);

  const updateAmount = (amount: number) => {
    setAmount(Number(amount));
    const vendorFeeBreakdown = getTrxFeeBreakdown(Number(amount), transactionFees);
    setFeeBreakdown({ ...vendorFeeBreakdown });
  };

  const netAmount =
    (feeBreakdown && selectedPaymentMethod && feeBreakdown[selectedPaymentMethod].netAmount) ??
    0;

  const paymentOptionEFTAvailable = paymentVendorsList[`${PaymentMethod.EFT}`] !== undefined;
  const paymentOptionInteracAvailable =
    paymentVendorsList[`${PaymentMethod.INTERAC}`] !== undefined;
  const paymentOptionCCAvailable =
    paymentVendorsList[`${PaymentMethod.CREDITCARD}`] !== undefined;

  const handleVendorChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    if (PaymentMethod.INTERAC === value) {
      setSelectedPaymentMethod(PaymentMethod.INTERAC);
    } else if (PaymentMethod.CREDITCARD === value) {
      setSelectedPaymentMethod(PaymentMethod.CREDITCARD);
    } else if (PaymentMethod.EFT === value) {
      setSelectedPaymentMethod(PaymentMethod.EFT);
    } else {
      setSelectedPaymentMethod(undefined);
    }
  };

  const handleCloseCheckout = (paymentComplete: boolean) => {
    if (paymentComplete) {
      handleComplete();
    }
    handleClose();
    setFeeBreakdown(undefined);
    setSelectedPaymentMethod(PaymentMethod.INTERAC);
  };

  const handleCCPayment = () => {
    switch (paymentVendorsList['CREDITCARD']) {
      case 'STRIPE': {
        setOpenStripeModal(true);
        break;
      }
      case 'FINIX': {
        setOpenFinixTokenModal(true);
        break;
      }
    }
  };

  const handleInteracPayment = async (fee: number, netAmount: number) => {
    try {
      if (!isBill) return;
      uiStore.showLoading();

      // create transaction log for payment
      const initiatePayment = () =>
        walletStore.initiatePayment(type, netAmount, fee, `${selectedPaymentMethod}`, {
          leaseId: prop.leaseId,
          billId: prop.billId,
        });

      setOpenInteracConfirmModal(true);
      const interacTargetWindowTitle = 'Realhaus <> Paydirect';
      const interacWindow = window.open(
        `${paths.__.interacProcess}?amount=${netAmount}`,
        interacTargetWindowTitle,
      );

      if (!interacWindow) {
        setOpenInteracConfirmModal(false);
        setPopupBlockerEnabled(true);
        console.error('Unable to continue interac payment. Popup blocker enabled');
        return;
      }

      const res = await initiatePayment();

      if (res?.paramountInteracPayload) {
        // make paramount commerce interac payment request
        const pData = res.paramountInteracPayload;
        setParamountInteracPayload({
          ...pData,
          target: interacTargetWindowTitle,
        });

        setTimeout(() => {
          if (pCommerceFormRef.current) {
            pCommerceFormRef.current.submit(); // Use the ref to submit the form
            interacWindow.focus();

            analyticsStore.trackBillPayment(
              netAmount,
              `${PaymentMethod.INTERAC}`,
              leaseBill?.type ?? '',
            );
          } else {
            console.error('Failed to get payment form ref');
            interacWindow.close();
          }
          setOpenInteracConfirmModal(false);
          handleCloseCheckout(true);
        }, 1000);
      } else if (res?.interacUrl) {
        analyticsStore.trackBillPayment(
          netAmount,
          `${PaymentMethod.INTERAC}`,
          leaseBill?.type ?? '',
        );
        window.open(res.interacUrl, interacTargetWindowTitle);
        setOpenInteracConfirmModal(false);
        handleCloseCheckout(true);
      } else {
        interacWindow?.close();
        const msg =
          'We were unable to pay your bill through interac at this time. Please try again later';
        const err = res?.errorMsg ? `${res.errorMsg}\n${msg}` : msg;
        uiStore.error(err);
        console.error(err);
        setOpenInteracConfirmModal(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      uiStore.hideLoading();
    }
  };

  const handleEFTPayment = async (fee: number, netAmount: number) => {
    if (!isBill) return;
    try {
      uiStore.showLoading();
      const res = await walletStore.initiatePayment(
        type,
        netAmount,
        fee,
        PaymentMethod.EFT,
        { leaseId: prop.leaseId, billId: prop.billId },
      );

      if (res?.errorMsg) {
        uiStore.error(res.errorMsg);
        return;
      }

      analyticsStore.trackBillPayment(
        netAmount,
        `${PaymentMethod.EFT}`,
        leaseBill?.type ?? '',
      );
      uiStore.success(
        'Payment initiated. Funds will be withdrawn from your bank in 4-5 business days.',
      );
      handleCloseCheckout(true);
    } catch (err) {
      console.error(err);
      uiStore.error(
        'We could not process your bank payment at this time. Please try again later.',
      );
    } finally {
      uiStore.hideLoading();
    }
  };

  const handleCloseInterac = () => { };
  const handleCloseFinix = (paymentCompleted: boolean) => {
    setOpenFinixTokenModal(false);
    handleCloseCheckout(paymentCompleted);
  };
  const handleCloseStripe = (paymentCompleted: boolean, checkoutSessionId?: string) => {
    setOpenStripeModal(false);
    if (!paymentCompleted && checkoutSessionId) {
      walletStore.cancelStripeCheckoutSession(checkoutSessionId);
    }
    if (paymentCompleted) {
      handleCloseCheckout(paymentCompleted);
    }
  };

  const handlePayment = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (!feeBreakdown || !selectedPaymentMethod || amount < 0) {
      return;
    }

    if (isCreditReportSeat && noOfSeats < 1) return;

    await checkAndProceed(async () => {
      const { fee, netAmount } = feeBreakdown[selectedPaymentMethod];
      console.debug(
        `Charging fee of ${fee} for amount ${amount} for a net amount of ${netAmount}`,
      );

      switch (selectedPaymentMethod) {
        case PaymentMethod.CREDITCARD:
          // ToDo: Integrate CC Payment Vendor
          handleCCPayment();
          break;
        case PaymentMethod.EFT:
          await handleEFTPayment(fee, netAmount);
          break;
        case PaymentMethod.INTERAC: {
          await handleInteracPayment(fee, netAmount);
          // await handleCheckPopupBlocked();
          break;
        }
      }
    });
  };

  const handleNoOfCreditReportSeatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isCreditReportSeat) {
      let numOfSeats = 1;

      const num = Number(e.target.value);

      if (!isNaN(num)) {
        numOfSeats = Math.max(1, Math.min(10, num));
      }

      setNoOfSeats(numOfSeats);
      updateAmount(Number(creditReportSeatAmount) * numOfSeats);
    }
  };

  const stripeInitPaymentCallback =
    (
      feeBreakdown:
        | { [key in PaymentMethod]: { fee: number; netAmount: number } }
        | undefined,
      selectedPaymentMethod: PaymentMethod | undefined,
      billId?: string,
      leaseId?: string,
      noOfCreditReportSeat?: number,
    ) =>
      () => {
        if (!selectedPaymentMethod || !feeBreakdown) {
          return;
        }
        const { fee, netAmount } = feeBreakdown[selectedPaymentMethod];

        return walletStore.initiatePayment(
          type,
          netAmount,
          fee,
          selectedPaymentMethod,
          {
            leaseId,
            billId,
            creditReportNoOfSeats: noOfCreditReportSeat,
          },
          {
            stripe: {
              returnUrl: window.location.href,
              successUrl: '',
            },
          },
        );
      };

  const finixInitPaymentCallback =
    (
      feeBreakdown:
        | { [key in PaymentMethod]: { fee: number; netAmount: number } }
        | undefined,
      selectedPaymentMethod: PaymentMethod | undefined,
      billId?: string,
      leaseId?: string,
      noOfCreditReportSeat?: number,
    ) =>
      (finixData: { token: string; sessionId: string }) => {
        if (!selectedPaymentMethod || !feeBreakdown) {
          return;
        }
        const { fee, netAmount } = feeBreakdown[selectedPaymentMethod];

        return walletStore.initiatePayment(
          type,
          netAmount,
          fee,
          selectedPaymentMethod,
          {
            leaseId,
            billId,
            creditReportNoOfSeats: noOfCreditReportSeat,
          },
          { finix: finixData },
        );
      };

  const componentCreditReportSeatsInput = () => {
    return (
      <>
        {isCreditReportSeat && (
          <Grid item xs={12}>
            <Typography variant='body1' gutterBottom>
              No. of seats
            </Typography>
            <TextField
              id='seats'
              value={noOfSeats}
              placeholder='No. of seats'
              style={{ marginTop: '0', paddingTop: '0' }}
              fullWidth
              type='number'
              inputProps={{ min: 1, max: 10 }}
              onChange={handleNoOfCreditReportSeatChange}
            />
          </Grid>
        )}
      </>
    );
  };

  const componentPaymentMethods = () => {
    return (
      <Grid item xs={12}>
        <Typography variant='body1' gutterBottom>
          Select Payment Method
        </Typography>
        <RadioGroup
          aria-label='fee-options'
          name='fee-options'
          value={selectedPaymentMethod}
          onChange={handleVendorChange}
        >
          {/* Check EFT Payment option available */}
          {showEFTPaymentOption && (
            <Paper
              elevation={0}
              variant='outlined'
              style={{
                padding: '0.5em',
                paddingLeft: '1em',
              }}
            >
              <FormControlLabel
                value={PaymentMethod.EFT}
                control={
                  <Grid
                    style={{
                      paddingRight: '1em',
                    }}
                  >
                    <Radio value={PaymentMethod.EFT} />
                    <SmallEFTIcon />
                  </Grid>
                }
                label='Pay By Bank'
              />

              <Grid container alignContent='center' style={{ padding: '8px 34px' }}>
                <Typography variant='body2' style={{ width: '100%' }}>
                  This option allows you to pay directly from your bank account, using the PAD (Pre-Authorized Debit) agreement you have set up with us. Funds will be withdrawn from your bank account within 4-5 business days.
                </Typography>
              </Grid>
            </Paper>
          )}

          {/* Check Interac Payment Option Available */}
          {showInteracPaymentOption && (
            <Paper
              elevation={0}
              variant='outlined'
              style={{
                padding: '0.5em',
                paddingLeft: '1em',
              }}
            >
              <FormControlLabel
                value={PaymentMethod.INTERAC}
                control={
                  <Grid
                    style={{
                      paddingRight: '1em',
                    }}
                  >
                    <Radio value={PaymentMethod.INTERAC} />
                    <SmallInteracIcon />
                  </Grid>
                }
                label={<Typography variant='body1'>INTERAC&reg;</Typography>}
              />

              <Grid container alignContent='center' style={{ padding: '8px 34px' }}>
                <Typography variant='body2' style={{ width: '100%' }}>
                  INTERAC is a registered trade-mark and the INTERAC logo is a
                  trade-mark of Interac Corp. Used under license.
                </Typography>
              </Grid>
            </Paper>
          )}

          {/* Check CreditCard Payment option available */}
          {showCCPaymentOption && (
            <Paper
              elevation={0}
              variant='outlined'
              style={{
                padding: '0.5em',
                paddingLeft: '1em',
              }}
            >
              <FormControlLabel
                value={PaymentMethod.CREDITCARD}
                control={
                  <Grid style={{ paddingRight: '1em' }}>
                    <Radio value={PaymentMethod.CREDITCARD} />
                    <SmallCreditCardIcon />
                  </Grid>
                }
                label='Credit Card (VISA, MASTERCARD, AMEX)'
              />
            </Paper>
          )}


        </RadioGroup>
        {/* Check if no available payment option */}
        {!showCCPaymentOption && !showEFTPaymentOption && !showInteracPaymentOption && (
          <Paper
            elevation={0}
            variant='outlined'
            style={{
              padding: '0.5em',
              paddingLeft: '1em',
            }}
          >
            <Typography variant='body1' align='center' gutterBottom>
              <b>No payment method available at this time.</b>
            </Typography>
          </Paper>
        )}
      </Grid>
    );
  };

  const showCCPaymentOption =
    paymentOptionCCAvailable === true &&
    isCreditReportSeat &&
    paymentOptionCcEnabled !== false;
  const showInteracPaymentOption =
    !isCreditReportSeat &&
    paymentOptionInteracAvailable === true &&
    paymentOptionInteracEnabled !== false;
  const showEFTPaymentOption =
    !isCreditReportSeat &&
    paymentOptionEFTAvailable === true &&
    autoDebitEnabled === true &&
    isAutoPayPolicyEnabled &&
    isPADSetupComplete &&
    paymentOptionBankEnabled !== false;

  const popupBlockerEnabledErrorMsg = () => {
    return (
      <>
        <Alert variant='outlined' severity='error'>
          <AlertTitle>Popup Blocker Detected</AlertTitle>
          <Typography style={{ marginBottom: '10px' }}>
            A new window needs to open for you to complete the payment, but your
            browser's popup blocker has stopped it. To continue, please disable your
            popup blocker or allow popups for this site in your browser settings.
          </Typography>

          <Typography>After enabling popups, refresh the page and try again.</Typography>
        </Alert>
      </>
    );
  };
  return (
    <>
      <RightDrawer isOpen={open} onOpen={() => { }} onClose={() => handleCloseCheckout(false)}>
        <div style={{ height: 'calc(100vh - 140px)' }}>
          <Grid container spacing={3}>
            <Grid item xs={1}></Grid>
            <Grid container item xs={10} spacing={3}>
              <Grid item xs={12}>
                <Typography variant='h4'>
                  {isCreditReportSeat
                    ? 'Purchase Credit Report seats'
                    : 'Pay your bill'}{' '}
                </Typography>
              </Grid>
              {popupBlockerEnabled && (
                <Grid item xs={12}>
                  {popupBlockerEnabledErrorMsg()}
                </Grid>
              )}
              <Grid container item spacing={4}>
                <Grid item xs={12}>
                  <Typography variant='body1' gutterBottom>
                    Amount
                  </Typography>
                  <InputFieldMoney
                    id='amount'
                    value={amount}
                    placeholder='Amount to Fund'
                    style={{ marginTop: '0', paddingTop: '0' }}
                    fullWidth
                    disabled
                    readOnly
                  />
                  {exceedsLimit(amount) && (
                    <Typography variant='body2' color='error' style={{ marginTop: '4px' }}>
                      {limitMessage}
                    </Typography>
                  )}
                </Grid>
                {componentCreditReportSeatsInput()}
                {componentPaymentMethods()}
                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    <Grid item xs={4}></Grid>
                    <Grid item container xs={8}>
                      <Grid container item spacing={2}>
                        <Grid item container xs={12}>
                          <Grid item xs={6}>
                            <Typography
                              variant='body1'
                              gutterBottom
                              align='left'
                            >
                              Amount
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant='body1'
                              gutterBottom
                              align='right'
                            >
                              <NumericFormat
                                value={amount}
                                prefix='$ '
                                suffix=' CAD'
                                displayType={'text'}
                                thousandSeparator={true}
                                decimalSeparator='.'
                                fixedDecimalScale
                                decimalScale={2}
                              />
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item container xs={12}>
                          <Grid item xs={6}>
                            <Typography
                              variant='body1'
                              gutterBottom
                              align='left'
                            >
                              Transaction Fee
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant='body1'
                              gutterBottom
                              align='right'
                            >
                              <NumericFormat
                                value={
                                  feeBreakdown &&
                                    selectedPaymentMethod
                                    ? feeBreakdown[
                                      selectedPaymentMethod
                                    ].fee
                                    : 0
                                }
                                prefix='$ '
                                suffix=' CAD'
                                displayType={'text'}
                                thousandSeparator={true}
                                decimalSeparator='.'
                                fixedDecimalScale
                                decimalScale={2}
                              />
                            </Typography>
                          </Grid>
                        </Grid>
                        <Divider
                          variant='fullWidth'
                          style={{ width: '100%' }}
                        />
                        <Grid item container xs={12}>
                          <Grid item xs={6}>
                            <Typography
                              variant='body1'
                              gutterBottom
                              align='left'
                            >
                              <b>Total</b>
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant='body1'
                              gutterBottom
                              align='right'
                            >
                              <b>
                                <NumericFormat
                                  value={netAmount}
                                  prefix='$ '
                                  suffix=' CAD'
                                  displayType={'text'}
                                  thousandSeparator={true}
                                  decimalSeparator='.'
                                  fixedDecimalScale
                                  decimalScale={2}
                                />
                              </b>
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            onClick={handlePayment}
                            color='primary'
                            variant='contained'
                            style={{ marginRight: '1rem' }}
                            disabled={
                              flaggedUser ||
                              (selectedPaymentMethod ==
                                PaymentMethod.INTERAC &&
                                popupBlockerEnabled) ||
                              !(amount > 0 && !!selectedPaymentMethod) ||
                              exceedsLimit(amount)
                            }
                          >
                            Pay
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={1}></Grid>
          </Grid>
        </div>
        {/* Footer fine print */}
        <Grid container spacing={3}>
          <Paper
            role='dialog'
            aria-modal='false'
            aria-label='Cookie banner'
            square
            variant='outlined'
            style={{
              borderTopWidth: '1px',
              borderWidth: '0',
              width: '100%',
            }}
          >
            <Grid
              container
              alignContent='center'
              style={{ padding: '8px 34px' }}
            ></Grid>
          </Paper>
        </Grid>
      </RightDrawer>

      <CompleteAccountSetupDialog {...gateDialogProps} />

      {/* Open Interac Notification Dialog */}
      <Dialog
        open={openInteracConfirmModal}
        onClose={handleCloseInterac}
        maxWidth='sm'
        fullWidth
      >
        <DialogContent>
          <div
            style={{
              minHeight: '250px',
              paddingTop: '10px',
              padding: '10px',
            }}
          >
            <Grid container>
              <Grid item style={{ width: '100%' }}>
                <div style={{ float: 'right', marginBottom: '15px' }}>
                  <LargeInteracIcon />
                </div>
              </Grid>
              <Grid item>
                <div>
                  <Typography variant='h5' align='center' gutterBottom>
                    Creating <i>Interac</i> e-Transfer® payment request for the
                    amount{' '}
                    <b>
                      <NumericFormat
                        value={netAmount}
                        prefix='$ '
                        suffix=' CAD'
                        displayType={'text'}
                        thousandSeparator={true}
                        decimalSeparator='.'
                        fixedDecimalScale
                        decimalScale={2}
                      />
                    </b>
                    .
                  </Typography>
                  <br />
                  <Typography variant='h5' align='center' gutterBottom>
                    You will be redirected shortly. Please Wait...
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions>
          <Paper
            role='dialog'
            aria-modal='false'
            aria-label='Cookie banner'
            square
            variant='outlined'
            style={{
              borderTopWidth: '1px',
              borderWidth: '0',
            }}
          >
            <Grid container style={{ padding: '8px 34px' }}>
              <Typography variant='body2'>
                INTERAC is a registered trade-mark and the INTERAC logo is a
                trade-mark of Interac Corp. Used under license.
              </Typography>
            </Grid>
          </Paper>
        </DialogActions>
      </Dialog>

      {selectedPaymentMethod === PaymentMethod.INTERAC && (
        <ParamountPaymentForm
          formRef={pCommerceFormRef}
          url={paramountInteracPayload?.paymentUrl!}
          mId={paramountInteracPayload?.merchantId!}
          dets={paramountInteracPayload?.details!}
          ivKey={paramountInteracPayload?.iv!}
          target={paramountInteracPayload?.target}
        />
      )}

      {/* Open Finix Tokenization Form */}
      {openFinixTokenModal &&
        selectedPaymentMethod === PaymentMethod.CREDITCARD &&
        !!feeBreakdown && (
          <FinixPaymentForm
            openFinixTokenModal={openFinixTokenModal}
            onClose={handleCloseFinix}
            billType={leaseBill?.type}
            feeBreakdown={feeBreakdown[PaymentMethod.CREDITCARD]}
            initPaymentCallback={finixInitPaymentCallback(
              feeBreakdown,
              selectedPaymentMethod,
              isBill ? prop.billId : undefined,
              isBill ? prop.leaseId : undefined,
              isCreditReportSeat ? noOfSeats : undefined,
            )}
          />
        )}
      {openStripeModal &&
        selectedPaymentMethod === PaymentMethod.CREDITCARD &&
        !!feeBreakdown && (
          <>
            <StripePaymentForm
              openStripeModal={openStripeModal}
              onClose={handleCloseStripe}
              billType={leaseBill?.type}
              feeBreakdown={feeBreakdown[PaymentMethod.CREDITCARD]}
              initPaymentCallback={stripeInitPaymentCallback(
                feeBreakdown,
                selectedPaymentMethod,
                isBill ? prop.billId : undefined,
                isBill ? prop.leaseId : undefined,
                isCreditReportSeat ? noOfSeats : undefined,
              )}
            />
          </>
        )}
    </>
  );
});

const ParamountPaymentForm: React.FC<{
  formRef: any;
  url: string;
  mId: string;
  dets: string;
  ivKey: string;
  target?: string;
}> = ({ formRef, url, mId, dets, ivKey, target }) => {
  const [formUrl, setFormUrl] = useState<string>('');
  const [merchantId, setMerchantId] = useState<string>('');
  const [detail, setDetail] = useState<string>('');
  const [iv, setIv] = useState<string>('');

  useEffect(() => {
    setFormUrl(url);
    setMerchantId(mId);
    setDetail(dets);
    setIv(ivKey);

    console.log('>>> ParamountPaymentForm props updated');
  }, [formRef, url, mId, dets, ivKey]);

  return (
    <form
      ref={formRef}
      method='POST'
      action={formUrl}
      target={target ?? '_blank'}
      id='paramount_interac_payment_form'
      style={{ display: 'none' }}
    >
      <input type='hidden' name='merchant_id' value={merchantId} />
      <input type='hidden' name='details' value={detail} />
      <input type='hidden' name='iv' value={iv} />
      <input type='submit' value='Interac_Payin' />
    </form>
  );
};
